import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Icon } from "@iconify/react";
import Swal from "sweetalert2";
import api from "../../../../utils/axios";
import { getReport } from "../../../../utils/ReportUtils";
import FileModalLocation from "../../Modal/FileModalLocation";

export default function ContractSignModal({ isOpen, onClose, contract, onSuccess }) {
  const [signType, setSignType] = useState("customer");
  const [password, setPassword] = useState("");
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [remark, setRemark] = useState("");
  const [loading, setLoading] = useState(false);

  //  โหลดข้อมูลสัญญา
  useEffect(() => {
    if (contract?.Contract_Id && isOpen) {
      api.get(`/get-contract/${contract.Contract_Id}`).then((res) => {
        setPassword(res.data?.header?.Sign_Password || "");
        setRemark(res.data?.header?.Sign_Remark || "");
      });
    }
  }, [contract, isOpen]);

  //  โหลดไฟล์จาก DB ( merge pending )
  const loadAttachedFiles = async () => {
    if (!contract?.Contract_Id) return;

    try {
      const res = await api.get(`/get-contract-file/${contract.Contract_Id}`);

      if (res.data.status === "OK") {
        const dbFiles = (res.data.result || []).map((f) => ({
          File_Id: f.F_Id,
          File_Name: f.File_Name,
          File_Url: f.File_Url,
          size: 1024,
          isNew: false,
        }));

        setSelectedFiles((prev) => {
          const pending = prev.filter((f) => f.isNew);
          return [...dbFiles, ...pending];
        });
      }
    } catch (err) {
      console.error("❌ Load Contract Files Error:", err);
    }
  };

  //  callback จาก FileModal
  const afterSaveFiles = async () => {
    await loadAttachedFiles();
    onSuccess?.(4);
    setShowAttachModal(false);
  };

  //  บันทึกไฟล์ทั้งหมด
  const handleSave = async () => {
    try {
      setLoading(true);

      const newFiles = selectedFiles.filter((f) => f.isNew && f.rawFile);

      if (newFiles.length > 0) {
        const formData = new FormData();
        newFiles.forEach((f) => formData.append("files", f.rawFile));

        formData.append("Comp_Id", contract?.Comp_Id || 1);
        formData.append("Upload_By", localStorage.getItem("DisplayName") || "System");
        formData.append("Remark", remark || "");

        const res = await api.post(
          `/upload-contract-file/${contract?.Contract_Id}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        if (res.data.status !== "OK") {
          Swal.fire("ผิดพลาด", "อัปโหลดไฟล์ไม่สำเร็จ", "error");
          return;
        }
      } else {
        //  ไม่มีไฟล์ใหม่ → อัปเดต remark อย่างเดียว
        await api.put(`/update-contract-remark/${contract?.Contract_Id}`, {
          Remark: remark || "",
          Update_By: localStorage.getItem("DisplayName") || "System",
        });
      }

      //  โหลดไฟล์ใหม่จาก DB + เคลียร์ไฟล์ pending
      await loadAttachedFiles();
      setSelectedFiles((prev) => prev.filter((f) => !f.isNew));

      // Swal.fire({
      //   icon: "success",
      //   title: "สำเร็จ!",
      //   text: "บันทึกข้อมูลเรียบร้อยแล้ว",
      //   timer: 1500,
      //   showConfirmButton: false,
      // });

      onSuccess?.(4);
      setShowAttachModal(false);
      onClose();
    } catch (err) {
      console.error("❌ Upload Error:", err);
      Swal.fire("ผิดพลาด", "ไม่สามารถบันทึกข้อมูลได้", "error");
    } finally {
      setLoading(false);
    }
  };

  //  โหลดไฟล์ตอน modal เปิด
  useEffect(() => {
    if (isOpen && contract?.Contract_Id) loadAttachedFiles();
  }, [isOpen, contract]);

  const fileCount = selectedFiles.length;
  const link = `${window.location.origin}/contract-sign/${contract?.Contract_Id}`;

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    Swal.fire({
      title: "คัดลอกแล้ว",
      icon: "success",
      timer: 900,
      showConfirmButton: false,
    });
  };

  if (!isOpen || !contract) return null;

  return ReactDOM.createPortal(
    <>
      {/*  MODAL หลัก */}
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-lg shadow-lg w-[480px] max-w-[95vw]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center bg-[#E13F57] text-white px-4 py-2 rounded-t-lg">
            <h2 className="font-semibold whitespace-nowrap">เซ็นสัญญา</h2>
            <button onClick={onClose}>
              <Icon icon="mdi:close" width="22" />
            </button>
          </div>

          <div className="p-5 space-y-4 min-w-0">
            {/*  toggle ลูกค้า/เจ้าหน้าที่ */}
            <div className="flex items-center gap-6 flex-nowrap">
              <label className="flex items-center gap-1.5 cursor-pointer shrink-0 min-w-[140px]">
                <input
                  type="radio"
                  name="signType"
                  value="customer"
                  checked={signType === "customer"}
                  onChange={() => setSignType("customer")}
                  className="w-3.5 h-3.5 accent-[#E13F57] shrink-0"
                />
                <span className="text-sm text-gray-800 whitespace-nowrap">เซ็นโดยลูกค้า</span>
              </label>

              <label className="flex items-center gap-1.5 cursor-pointer shrink-0 min-w-[160px]">
                <input
                  type="radio"
                  name="signType"
                  value="staff"
                  checked={signType === "staff"}
                  onChange={() => setSignType("staff")}
                  className="w-3.5 h-3.5 accent-[#E13F57] shrink-0"
                />
                <span className="text-sm text-gray-800 whitespace-nowrap">เซ็นโดยเจ้าหน้าที่</span>
              </label>
            </div>

            {/*  ลูกค้า */}
            {signType === "customer" && (
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="min-w-0 flex-1 text-blue-600 underline break-all hover:text-blue-800"
                  >
                    {link}
                  </a>
                  <button onClick={() => handleCopy(link)} className="shrink-0">
                    <Icon icon="mdi:content-copy" width="18" />
                  </button>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium shrink-0">รหัสผ่าน:</span>
                  <span className="text-gray-700 break-all">{password || "กำลังโหลด..."}</span>
                  {password && (
                    <button onClick={() => handleCopy(password)}>
                      <Icon icon="mdi:content-copy" width="18" />
                    </button>
                  )}
                </div>

                <p className="text-xs text-gray-500 pt-2 whitespace-normal">
                  ลูกค้าหกรลิงก์ → กรอกรหัสผ่าน → เซ็นออนไลน์ได้ทันที
                </p>
              </div>
            )}

            {/*  เจ้าหน้าที่ */}
            {signType === "staff" && (
              <div className="space-y-4">
                {/*  Print */}
                <div className="flex items-center gap-2 flex-nowrap">
                  <label className="font-medium shrink-0 whitespace-nowrap min-w-[140px]">ดาวน์โหลดสัญญา</label>
                  <button
                    onClick={() => getReport("contract", contract?.Contract_Id)}
                    className="btn btn-link btn-sm px-3"
                  >
                    <i className="fa fa-print" style={{ fontSize: "16px" }}></i>
                  </button>
                </div>

                {/*  ดู / เพิ่มไฟล์ */}
                <div className="relative inline-block">
                  <button
                    onClick={async () => {
                      await loadAttachedFiles();
                      setShowAttachModal(true);
                    }}
                    className="flex items-center gap-2 bg-[#F17F25] hover:bg-[#d96a1f] text-white px-4 py-2 rounded-lg whitespace-nowrap"
                  >
                    ดู/เพิ่มไฟล์แนบสัญญา
                  </button>

                  {fileCount > 0 && (
                    <span
                      className="
                      absolute -top-2 -right-2
                      bg-red-600 text-white
                      text-xs font-bold
                      px-2 py-[1px]
                      rounded-full shadow"
                    >
                      {fileCount}
                    </span>
                  )}
                </div>

                {/*  Remark */}
                <div>
                  <label className="block font-medium mb-1 whitespace-nowrap min-w-[140px] w-fit">หมายเหตุเพิ่มเติม</label>
                  <textarea
                    rows={3}
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                    className="w-full border border-gray-300 rounded-md p-2 resize-none text-sm"
                  />
                </div>
              </div>
            )}
          </div>

          {/*  Footer */}
          <div className="border-t px-5 py-3 flex justify-end">
            {signType === "staff" ? (
              <button
                onClick={handleSave}
                disabled={loading}
                className={`${loading ? "bg-gray-400" : "bg-[#E13F57] hover:bg-[#c8304b]"} text-white font-medium px-6 py-2 rounded-lg`}
              >
                {loading ? "กำลังบันทึก..." : "บันทึก"}
              </button>
            ) : (
              <button
                onClick={onClose}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium px-6 py-2 rounded-lg"
              >
                ปิด
              </button>
            )}
          </div>
        </div>
      </div>

      {/*  FileModal อยู่บนสุด */}
      {showAttachModal && contract?.Contract_Id && (
        <div className="fixed inset-0 z-[9999]">
          <FileModalLocation
            mode="E"
            showViewAttachModal={showAttachModal}
            setShowViewAttachModal={setShowAttachModal}
            selectedFiles={selectedFiles}
            setSelectedFiles={setSelectedFiles}
            Doc_Id={contract?.Contract_Id}
            docCodeId={20}
            onAfterSave={afterSaveFiles}
          />
        </div>
      )}
    </>,
    document.body
  );
}